import { useCallback, useMemo } from 'react'

import * as Prismic from '@prismicio/client'
import { type RichTextField } from '@prismicio/client'
import { type UseQueryResult } from '@tanstack/react-query'

import { usePrismic } from '@dhl/prismic'
import { isProd } from '@dhl/utils'
import { useLocalStorage } from '@dhl/use-storage'

import { type PrismicMaintenanceMessage, type MaintenanceMessageItem, type MaintenanceTags } from './maintenance.js'

type PlatformTag = 'mobile' | 'desktop'

const maintenanceMessagePredicate = Prismic.filter.at('document.type', 'maintenance-message')
const documentTagsPredicate = (...tags: MaintenanceTags[]): string => Prismic.filter.at('document.tags', tags)
const excludePlatformTagsPredicate = (platformTagToExclude: PlatformTag): string =>
  Prismic.filter.not('document.tags', [platformTagToExclude])

const excludeAcceptTag = (): string => Prismic.filter.not('document.tags', ['accept'])

const unwrapPrismicDataFromDocument = (
  document: Prismic.PrismicDocument<PrismicMaintenanceMessage>
): {
  id: string
  last_publication_date: string
  maintenance_title: RichTextField
  maintenance_body: RichTextField
} => ({
  id: document.id,
  last_publication_date: document.last_publication_date,
  maintenance_title: document.data.maintenance_title,
  maintenance_body: document.data.maintenance_body,
})

const unwrapPrismicDataFromResults = (
  documents?: Prismic.PrismicDocument<PrismicMaintenanceMessage>[]
): ReturnType<typeof unwrapPrismicDataFromDocument>[] =>
  documents?.map((document) => unwrapPrismicDataFromDocument(document)) ?? []

const createKey = (tag: MaintenanceTags, id: string, lastPublicationDate?: string): string =>
  [tag, id, lastPublicationDate].filter(Boolean).join(':')

export const usePrismicMaintenanceMessage = (
  endpoint: string,
  tag: MaintenanceTags,
  lang = '',
  isMobile = false
): UseQueryResult<MaintenanceMessageItem | undefined> => {
  const platformPredicate = isMobile ? excludePlatformTagsPredicate('desktop') : excludePlatformTagsPredicate('mobile')
  const environmentPredicate = isProd() ? excludeAcceptTag() : ''
  const query = useMemo(
    () =>
      [maintenanceMessagePredicate, documentTagsPredicate(tag), platformPredicate, environmentPredicate].filter(
        Boolean
      ),
    [tag, platformPredicate, environmentPredicate]
  )
  const options: Prismic.QueryParams = useMemo(
    () => ({
      orderings: [{ field: 'document.last_publication_date', direction: 'desc' }],
      lang: lang?.toLowerCase().replace('_', '-'),
    }),
    [lang]
  )

  return usePrismic<PrismicMaintenanceMessage, MaintenanceMessageItem | undefined>(
    {
      endpoint,
      query,
      options,
    },
    {
      query: {
        select: (response): MaintenanceMessageItem | undefined => {
          const [firstDoc] = unwrapPrismicDataFromResults(response?.results)

          if (!firstDoc) {
            return undefined
          }

          return {
            id: firstDoc.id,
            lastPublicationDate: firstDoc.last_publication_date,
            title: Prismic.asText(firstDoc.maintenance_title),
            body: Prismic.asText(firstDoc.maintenance_body),
          }
        },
      },
    }
  )
}

type UseDismissableMaintenanceMessagesResult = {
  isMessageDismissed: (tag: MaintenanceTags, id: string, lastPublicationDate?: string) => boolean
  dismissMessage: (tag: MaintenanceTags, id: string, lastPublicationDate?: string) => void
}

export const useDismissableMaintenanceMessages = (): UseDismissableMaintenanceMessagesResult => {
  const [dismissagedMessages, setDismissedMessages] = useLocalStorage<string[]>('maintenance-messages-dismissed', [])

  const isMessageDismissed = useCallback(
    (tag: MaintenanceTags, id: string, lastPublicationDate?: string): boolean =>
      dismissagedMessages.includes(createKey(tag, id, lastPublicationDate)),
    [dismissagedMessages]
  )

  const dismissMessage = useCallback(
    (tag: MaintenanceTags, id: string, lastPublicationDate?: string) => {
      const key = createKey(tag, id, lastPublicationDate)
      setDismissedMessages([...dismissagedMessages.filter((m) => m !== key), key])
    },
    [dismissagedMessages, setDismissedMessages]
  )

  return { isMessageDismissed, dismissMessage }
}
